import { useAuth0 } from '@auth0/auth0-react';
import { API_ENDPOINTS, AUTH_SESSION_DEATH_ERROR_CODES } from '@consts';
import { EventType, eventEmitter } from '@event-emitter';
import {
  AssignmentBlockUserInputModel,
  AssignmentsResponseModel,
  CreateEntityResponseModel,
  CreateResponseModel,
  SuccessResponseModel,
  TaskAssignmentCreateModel,
  VersionedEntityType
} from '@models';
import {
  AccountApprovalCreateModel,
  AccountModel,
  AccountProfileModel,
  AccountUpdatePatchModel
} from '@models/accounts';
import { AffiliateLeadsModel, ReferralClaimResultModel } from '@models/affiliate';
import {
  AssignmentBlockOutputCreateModel,
  AssignmentModel,
  BlockOutputResultAsMentorItem,
  DeprecatedAssignmentModel,
  EvaluateBlockInputResponse
} from '@models/assignments';
import {
  CancelChatbotReplyResponse,
  ConversationModel,
  ConversationSummariesResponseModel,
  CreatedMessageResponse,
  GetAttachmentsParamModel,
  MessageModel
} from '@models/conversations';
import { GoalPackageDetailModel } from '@models/goalPackages';
import {
  ComboGoalGraphModel,
  GoalCategoryCid,
  GoalEarningsModel,
  GoalModel,
  GoalPriceDetailsModel,
  GoalPriceModel,
  GoalPricePatchModel,
  GoalVisibilityVectorModel,
  MentorEarningsModel
} from '@models/goals';
import {
  CreateGoalSubscriptionModel,
  CreatedGoalSubscriptionModel,
  GetGoalSubscriptionsParamsModel,
  GoalSubscriptionModel,
  GoalSubscriptionVerificationModel,
  GoalSubscriptionsSummaryModel
} from '@models/goalSubscriptions';
import { GetGoalTOCParamsModel, GoalTOCModel } from '@models/goalTocs';
import { ImprvCoinsLedgerResponseModel, ImprvCoinsPackageModel } from '@models/imprvCoins';
import { LegalDocumentModel, LegalDocumentType } from '@models/legalDocuments';
import { NotificationIntentModel } from '@models/marketing';
import {
  CreateMediaitemModel,
  MediaitemModel,
  MediaitemSignedUploadModel
} from '@models/mediaitems';
import {
  GoalAccessGrantModel,
  OrderInvoiceModel,
  OrdersResponseModel,
  UserOrderModel
} from '@models/orders';
import { PaymentSessionCreateModel, PaymentSessionModel } from '@models/payments';
import { GoalProgressModel, TaskBlocksProgressModel, TaskProgressModel } from '@models/progress';
import { SearchResultModel } from '@models/search';
import { SuccessCreatedEntity } from '@models/shared';
import {
  CreateSkillGraphModel,
  SkillEdgeModel,
  SkillEdgePatchModel,
  SkillGraphModel,
  SkillNodeModel,
  SkillNodePatchModel
} from '@models/skillGraphs';
import { TaskModel, TaskValidationSummaryModel } from '@models/tasks';
import * as Sentry from '@sentry/nextjs';
import * as _ from 'lodash';
import { BaseHttpService } from './baseHttpService';

class ImprvApiService extends BaseHttpService {
  private static instance: ImprvApiService;
  private getTokenProvider: () => Promise<string>;

  private constructor(getTokenProvider: () => Promise<string>) {
    super(process.env.API_BASE_URL || process.env.NEXT_PUBLIC_API_BASE_URL);
    this.getTokenProvider = getTokenProvider;
  }

  public static getInstance(getTokenProvider: () => Promise<string>): ImprvApiService {
    if (!getTokenProvider) {
      throw new Error('getTokenProvider is required');
    }

    if (!ImprvApiService.instance) {
      ImprvApiService.instance = new ImprvApiService(getTokenProvider);
    }
    return ImprvApiService.instance;
  }

  private async _handleResponse<T>(response: Response): Promise<T> {
    if (response.ok) return response.json();

    const body = await response.json().catch(() => undefined);
    const error = new Error(`HTTP error ${response.status}`);
    error['status'] = response.status;
    error['errorCode'] = body?.errorCode as string | undefined;

    if (
      response.status === 401 &&
      error['errorCode'] &&
      AUTH_SESSION_DEATH_ERROR_CODES.has(error['errorCode'])
    ) {
      eventEmitter.emit(EventType.AUTH_SESSION_INVALID, {});
    }

    throw error;
  }

  /**
   * ACCOUNTS
   */
  public getAccountMe = async (): Promise<AccountModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(API_ENDPOINTS.accountsMe(), {}, {}, bearerToken);
    return this._handleResponse<AccountModel>(response);
  };

  public updateAccountMe = async (
    accountMe: AccountUpdatePatchModel
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPatch(API_ENDPOINTS.accountsMe(), {}, accountMe, bearerToken);
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public getAccountProfile = async (id: string): Promise<AccountProfileModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(API_ENDPOINTS.accountsProfile(id), {}, {}, bearerToken);
    return this._handleResponse<AccountProfileModel>(response);
  };

  public markIntroAsDone = async (): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.accountsMarkIntroAsDone(),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public createAccountApprovals = async (
    approvals: AccountApprovalCreateModel[]
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.accountsApprovals(),
      {},
      { approvals: approvals },
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  /**
   * AFFILIATE
   */
  public registerReferral = async (referralCode: string): Promise<ReferralClaimResultModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.accountsReferral(),
      {},
      { referralCode },
      bearerToken
    );
    return this._handleResponse<ReferralClaimResultModel>(response);
  };

  public getAccountEarnings = async (): Promise<AffiliateLeadsModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(API_ENDPOINTS.accountsEarnings(), {}, {}, bearerToken);
    return this._handleResponse<AffiliateLeadsModel>(response);
  };

  public getAccountMentorEarnings = async (): Promise<MentorEarningsModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.accountsMentorEarnings(),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<MentorEarningsModel>(response);
  };

  public getAccountOrders = async (): Promise<UserOrderModel[]> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(API_ENDPOINTS.accountsOrders(), {}, {}, bearerToken);
    return this._handleResponse<UserOrderModel[]>(response);
  };

  public generateOrderInvoice = async (orderId: string): Promise<OrderInvoiceModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(API_ENDPOINTS.orderInvoice(orderId), {}, {}, bearerToken);
    return this._handleResponse<OrderInvoiceModel>(response);
  };

  public getOrderInvoice = async (orderId: string): Promise<OrderInvoiceModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(API_ENDPOINTS.orderInvoice(orderId), {}, {}, bearerToken);
    return this._handleResponse<OrderInvoiceModel>(response);
  };

  public downloadOrderInvoicePdf = async (orderId: string): Promise<Blob> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.orderInvoicePdf(orderId),
      {},
      {},
      bearerToken
    );
    if (!response.ok) {
      return this._handleResponse<never>(response);
    }
    return response.blob();
  };

  /**
   * LEGAL DOCUMENTS
   */
  public getLegalDocuments = async (
    types: LegalDocumentType[]
  ): Promise<{ legalDocuments: LegalDocumentModel[] }> => {
    const bearerToken = await this.getOptionalAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.legalDocuments(types),
      {},
      { types },
      bearerToken
    );
    return this._handleResponse<{ legalDocuments: LegalDocumentModel[] }>(response);
  };

  /**
   * ASSIGNMENTS
   */

  public evaluateBlockUserInputForNode = async (
    nodeEntityId: string,
    goalSubscriptionId: number,
    blockUserInput: AssignmentBlockUserInputModel
  ): Promise<EvaluateBlockInputResponse> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.assignmentNodeEvaluateBlockInput(nodeEntityId, goalSubscriptionId),
      {},
      blockUserInput,
      bearerToken
    );
    return this._handleResponse<EvaluateBlockInputResponse>(response);
  };

  public getAssignmentById = async (assignmentId: string): Promise<DeprecatedAssignmentModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.assignmentById(assignmentId),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<DeprecatedAssignmentModel>(response);
  };

  /**
   * Creates a new task assignment
   */
  public createTaskAssignment = async (
    assignment: TaskAssignmentCreateModel
  ): Promise<CreateResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(API_ENDPOINTS.assignments(), {}, assignment, bearerToken);
    return this._handleResponse<CreateResponseModel>(response);
  };

  public getTask = async (
    taskEntityId: string,
    params?: { maxVersionType?: VersionedEntityType }
  ): Promise<TaskModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      `/tasks/${taskEntityId}`,
      {},
      { maxVersionType: params?.maxVersionType },
      bearerToken
    );
    return this._handleResponse<TaskModel>(response);
  };

  public publishTask = async (taskEntityId: string): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(`/tasks/${taskEntityId}/publish`, {}, {}, bearerToken);
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public updateTaskValidationSummary = async (
    taskEntityId: string,
    payload: { versionType: VersionedEntityType } & TaskValidationSummaryModel
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPut(
      `/tasks/${taskEntityId}/validation-summary`,
      {},
      payload,
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };
  /**
   * PROGRESS
   */
  public getTaskProgress = async (taskEntityId: string): Promise<TaskProgressModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.progressByTaskEntityId(taskEntityId),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<TaskProgressModel>(response);
  };

  public getGoalProgress = async (goalEntityId: string): Promise<GoalProgressModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.progressByGoalEntityId(goalEntityId),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<GoalProgressModel>(response);
  };

  public getTaskBlocksProgress = async (
    taskEntityId: string,
    goalSubscriptionId?: number
  ): Promise<TaskBlocksProgressModel> => {
    const bearerToken = await this.getAPIToken();
    const queryParams = goalSubscriptionId != null ? { goalSubscriptionId } : {};
    const response = await this.httpGet(
      API_ENDPOINTS.progressBlocksByTaskEntityId(taskEntityId),
      {},
      queryParams,
      bearerToken
    );
    return this._handleResponse<TaskBlocksProgressModel>(response);
  };

  public getAssignmentsByNode = async (
    nodeEntityId: string,
    goalSubscriptionId: number
  ): Promise<AssignmentsResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.assignments(),
      {},
      { nodeEntityId, goalSubscriptionId },
      bearerToken
    );
    return this._handleResponse<AssignmentsResponseModel>(response);
  };

  public getAssignmentByNode = async (
    nodeEntityId: string,
    goalSubscriptionId: number,
    options?: { signal?: AbortSignal }
  ): Promise<AssignmentModel> => {
    const bearerToken = await this.getAPIToken();
    const fetchOptions = options?.signal ? { signal: options.signal } : {};
    const response = await this.httpGet(
      API_ENDPOINTS.assignmentByNode(nodeEntityId),
      fetchOptions,
      { goalSubscriptionId },
      bearerToken
    );
    return this._handleResponse<AssignmentModel>(response);
  };

  public getBlockOutputResultsAsMentor = async (
    nodeEntityId: string,
    userId?: number
  ): Promise<BlockOutputResultAsMentorItem[]> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.assignmentNodeBlockOutputResultsAsMentor(nodeEntityId),
      {},
      { ...(!_.isNil(userId) ? { userIds: [userId] } : {}) },
      bearerToken
    );
    return this._handleResponse<BlockOutputResultAsMentorItem[]>(response);
  };

  public getAssignmentWithBlockOutputs = async (
    nodeEntityId: string,
    goalSubscriptionId: number
  ): Promise<AssignmentsResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.assignments(),
      {},
      { nodeEntityId, goalSubscriptionId },
      bearerToken
    );
    return this._handleResponse<AssignmentsResponseModel>(response);
  };

  public createBlockOutputsAsMentor = async (
    nodeEntityId: string,
    userId: number,
    goalSubscriptionId: number,
    blockOutputs: AssignmentBlockOutputCreateModel[]
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.assignmentNodeBlockOutputsAsMentor(nodeEntityId, goalSubscriptionId),
      {},
      { user_id: userId, block_outputs: blockOutputs },
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public createBlockOutputsForNode = async (
    nodeEntityId: string,
    goalSubscriptionId: number,
    blockOutputs: AssignmentBlockOutputCreateModel[],
    keepAlive: boolean = false
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    let options = {};
    if (keepAlive) {
      options = {
        keepalive: true
      };
    }
    const response = await this.httpPost(
      API_ENDPOINTS.assignmentNodeBlockOutputs(nodeEntityId, goalSubscriptionId),
      options,
      { block_outputs: blockOutputs },
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  /**
   * GOALS
   */
  public getComboGoalGraph = async (goalEntityId: string): Promise<ComboGoalGraphModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.getComboGoalGraph(goalEntityId),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<ComboGoalGraphModel>(response);
  };

  public getGoals = async (params?: {
    query?: string;
    semanticQuery?: string;
    filters?: Record<any, any>;
    maxVersionType?: VersionedEntityType;
    comboGoalEntityId?: string;
    childGoalEntityId?: string;
    goalEntityIds?: string[];
    excludeSubscribed?: boolean;
    canEnroll?: boolean;
    categoryCid?: GoalCategoryCid;
    withRecommendations?: boolean;
  }): Promise<{ goals: GoalModel[] }> => {
    const qParams: {
      maxVersionType: VersionedEntityType;
      query?: string;
      semanticQuery?: string;
      [key: string]: any;
    } = {
      maxVersionType: params?.maxVersionType || VersionedEntityType.PUBLISHED
    };

    if (params?.query) {
      qParams.query = params.query;
    }
    if (params?.semanticQuery) {
      qParams.semanticQuery = params.semanticQuery;
    }
    if (params?.filters) {
      Object.assign(qParams, params.filters);
    }
    if (params?.comboGoalEntityId) {
      qParams.comboGoalEntityId = params.comboGoalEntityId;
    }
    if (params?.childGoalEntityId) {
      qParams.childGoalEntityId = params.childGoalEntityId;
    }
    if (params?.goalEntityIds) {
      qParams.goalEntityIds = params.goalEntityIds;
    }
    if (params?.excludeSubscribed) {
      qParams.excludeSubscribed = params.excludeSubscribed;
    }
    if (params?.canEnroll) {
      qParams.canEnroll = params.canEnroll;
    }
    if (params?.categoryCid) {
      qParams.categoryCid = params.categoryCid;
    }
    if (_.isBoolean(params?.withRecommendations)) {
      qParams.withRecommendations = params.withRecommendations;
    }

    const bearerToken = await this.getOptionalAPIToken();
    const response = await this.httpGet(API_ENDPOINTS.getGoals(), {}, qParams, bearerToken);
    return this._handleResponse<{ goals: GoalModel[] }>(response);
  };

  public getGoal = async (
    goalEntityId: string,
    maxVersionType: VersionedEntityType = VersionedEntityType.PUBLISHED
  ): Promise<GoalModel> => {
    const bearerToken = await this.getOptionalAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.getGoal(goalEntityId),
      {},
      { maxVersionType },
      bearerToken
    );
    return this._handleResponse<GoalModel>(response);
  };

  public getGoalPackage = async (packageId: string): Promise<GoalPackageDetailModel> => {
    const bearerToken = await this.getOptionalAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.getGoalPackage(packageId),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<GoalPackageDetailModel>(response);
  };

  public getGoalTOC = async (params: GetGoalTOCParamsModel): Promise<GoalTOCModel> => {
    const bearerToken = await this.getAPIToken();
    const { goalEntityId, ...queryParams } = params;
    const response = await this.httpGet(
      API_ENDPOINTS.getGoalTOC(goalEntityId),
      {},
      queryParams,
      bearerToken
    );
    return this._handleResponse<GoalTOCModel>(response);
  };

  public getGoalsRecommendationsCount = async (): Promise<{ count: number }> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.getGoalsRecommendationsCount(),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<{ count: number }>(response);
  };

  public getGoalPrices = async (goalEntityId: string): Promise<GoalPriceModel[]> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.getGoalPrices(goalEntityId),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<GoalPriceModel[]>(response);
  };

  public createGoalPrice = async (
    goalEntityId: string,
    goalPrice: GoalPricePatchModel
  ): Promise<CreateEntityResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.createGoalPrice(goalEntityId),
      {},
      goalPrice,
      bearerToken
    );
    return this._handleResponse<CreateEntityResponseModel>(response);
  };

  public getGoalPrice = async (
    goalEntityId: string,
    goalPriceEntityId: string,
    maxVersionType: VersionedEntityType = VersionedEntityType.PUBLISHED
  ): Promise<GoalPriceModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.getGoalPrice(goalEntityId, goalPriceEntityId),
      {},
      { maxVersionType },
      bearerToken
    );
    return this._handleResponse<GoalPriceModel>(response);
  };

  public updateGoalPrice = async (
    goalEntityId: string,
    goalPriceEntityId: string,
    goalPrice: GoalPricePatchModel
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPatch(
      API_ENDPOINTS.updateGoalPrice(goalEntityId, goalPriceEntityId),
      {},
      goalPrice,
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public updateGoal = async (
    goalEntityId: string,
    patch: Partial<Pick<GoalModel, 'privacy'>>
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPut(
      API_ENDPOINTS.updateGoal(goalEntityId),
      {},
      patch,
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public getGoalVisibilityVector = async (
    goalEntityId: string
  ): Promise<GoalVisibilityVectorModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.goalVisibilityVector(goalEntityId),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<GoalVisibilityVectorModel>(response);
  };

  public updateGoalVisibilityVector = async (
    goalEntityId: string,
    vector: GoalVisibilityVectorModel
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPut(
      API_ENDPOINTS.goalVisibilityVector(goalEntityId),
      {},
      vector,
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public getGoalLandingPage = async (
    goalEntityId: string,
    maxVersionType?: VersionedEntityType
  ): Promise<GoalModel> => {
    const bearerToken = await this.getOptionalAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.goalLandingPage(goalEntityId),
      {},
      { maxVersionType },
      bearerToken
    );
    return this._handleResponse<GoalModel>(response);
  };

  public publishGoalPrice = async (
    goalEntityId: string,
    goalPriceEntityId: string,
    goalPrice: GoalPricePatchModel
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.publishGoalPrice(goalEntityId, goalPriceEntityId),
      {},
      goalPrice,
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public calculateGoalPriceDetails = async (
    pricing: GoalPriceDetailsModel
  ): Promise<GoalPriceDetailsModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.calculateGoalPriceDetails(),
      {},
      pricing,
      bearerToken
    );
    return this._handleResponse<GoalPriceDetailsModel>(response);
  };

  public getGoalEarnings = async (goalEntityId: string): Promise<GoalEarningsModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.goalEarnings(goalEntityId),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<GoalEarningsModel>(response);
  };

  /**
   * GOAL SUBSCRIPTIONS
   */
  public createGoalSubscription = async (
    subscription: CreateGoalSubscriptionModel
  ): Promise<CreatedGoalSubscriptionModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.goalSubscriptions(),
      {},
      subscription,
      bearerToken
    );
    return this._handleResponse<CreatedGoalSubscriptionModel>(response);
  };

  public getGoalSubscriptions = async (
    params: GetGoalSubscriptionsParamsModel
  ): Promise<{ goalSubscriptions: GoalSubscriptionModel[] }> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(API_ENDPOINTS.goalSubscriptions(), {}, params, bearerToken);
    return this._handleResponse<{ goalSubscriptions: GoalSubscriptionModel[] }>(response);
  };

  public getOrders = async (): Promise<OrdersResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(API_ENDPOINTS.orders(), {}, {}, bearerToken);
    return this._handleResponse<OrdersResponseModel>(response);
  };

  public getImprvCoinsLedger = async (): Promise<ImprvCoinsLedgerResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(API_ENDPOINTS.imprvCoinsLedger(), {}, {}, bearerToken);
    return this._handleResponse<ImprvCoinsLedgerResponseModel>(response);
  };

  public getGoalAccessGrants = async (goalEntityId: string): Promise<GoalAccessGrantModel[]> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.orderGrants(),
      {},
      { goalEntityId },
      bearerToken
    );
    return this._handleResponse<GoalAccessGrantModel[]>(response);
  };

  public createGoalAccessGrant = async (
    goalEntityId: string,
    email: string
  ): Promise<GoalAccessGrantModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.orderGrants(),
      {},
      { goalEntityId, email },
      bearerToken
    );
    return this._handleResponse<GoalAccessGrantModel>(response);
  };

  public getGoalSubscriptionsByGoalEntityId = async (
    goalEntityId: string,
    statuses?: string[]
  ): Promise<GoalSubscriptionModel[]> => {
    const bearerToken = await this.getAPIToken();
    const params: { statuses?: string[]; goalEntityIds?: string[] } = {
      goalEntityIds: [goalEntityId]
    };
    if (statuses && statuses.length > 0) {
      params.statuses = statuses;
    }
    const response = await this.httpGet(
      API_ENDPOINTS.goalSubscriptionsAsMentor(),
      {},
      params,
      bearerToken
    );
    return this._handleResponse<GoalSubscriptionModel[]>(response);
  };

  public getGoalSubscription = async (
    goalSubscriptionId: number
  ): Promise<GoalSubscriptionModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.goalSubscription(goalSubscriptionId),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<GoalSubscriptionModel>(response);
  };

  public getGoalSubscriptionVerification = async (
    verificationId: string
  ): Promise<GoalSubscriptionVerificationModel> => {
    const bearerToken = await this.getOptionalAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.goalSubscriptionVerification(verificationId),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<GoalSubscriptionVerificationModel>(response);
  };

  public getGoalSubscriptionsSummary = async (): Promise<GoalSubscriptionsSummaryModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.goalSubscriptionsSummary(),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<GoalSubscriptionsSummaryModel>(response);
  };

  public updateGoalSubscription = async (
    goalSubscriptionId: number,
    payload: { status?: string; priority?: string; deadline?: string }
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPut(
      API_ENDPOINTS.updateGoalSubscription(goalSubscriptionId),
      {},
      payload,
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  /**
   * SEARCH
   */
  public search = async (goalSubscriptionId: number, query: string): Promise<SearchResultModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.search(),
      {},
      { goalSubscriptionId, query },
      bearerToken
    );
    return this._handleResponse<SearchResultModel>(response);
  };

  /**
   * CONVERSATIONS
   */
  public getInboxSummaries = async (params?: {
    cursor?: string;
    size?: number;
    includeCleaned?: boolean;
    userIds?: number[];
    goalEntityIds?: string[];
  }): Promise<ConversationSummariesResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.conversations_summaries_inbox(),
      {},
      {
        cursor: params?.cursor,
        size: params?.size,
        includeCleaned: params?.includeCleaned,
        userIds: params?.userIds,
        goalEntityIds: params?.goalEntityIds
      },
      bearerToken
    );
    return this._handleResponse<ConversationSummariesResponseModel>(response);
  };

  public cleanInboxConversation = async (conversationId: number): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.conversations_summaries_inbox_clean(conversationId),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public uncleanInboxConversation = async (
    conversationId: number
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.conversations_summaries_inbox_unclean(conversationId),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public getConversations = async (): Promise<{ conversations: ConversationModel[] }> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet('/conversations', {}, {}, bearerToken);
    return this._handleResponse<{ conversations: ConversationModel[] }>(response);
  };

  public getConversation = async (
    conversationId: number,
    options?: { signal?: AbortSignal }
  ): Promise<ConversationModel> => {
    const bearerToken = await this.getAPIToken();
    const fetchOptions = options?.signal ? { signal: options.signal } : {};
    const response = await this.httpGet(
      `/conversations/${conversationId}`,
      fetchOptions,
      {},
      bearerToken
    );
    return this._handleResponse<ConversationModel>(response);
  };

  public getConversationMessages = async (
    conversationId: number,
    params?: { size?: number; minCreatedAt?: string; maxCreatedAt?: string },
    options?: { signal?: AbortSignal }
  ): Promise<{ messages: MessageModel[] }> => {
    const bearerToken = await this.getAPIToken();
    const queryParams: Record<string, string> = {};
    if (params?.size) queryParams.size = params.size.toString();
    if (params?.minCreatedAt) queryParams.minCreatedAt = params.minCreatedAt;
    if (params?.maxCreatedAt) queryParams.maxCreatedAt = params.maxCreatedAt;

    const fetchOptions = options?.signal ? { signal: options.signal } : {};
    const response = await this.httpGet(
      `/conversations/${conversationId}/messages`,
      fetchOptions,
      queryParams,
      bearerToken
    );
    return this._handleResponse<{ messages: MessageModel[] }>(response);
  };

  public getConversationMessage = async (
    conversationId: number,
    messageId: number
  ): Promise<MessageModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      `/conversations/${conversationId}/messages/${messageId}`,
      {},
      {},
      bearerToken
    );
    return this._handleResponse<MessageModel>(response);
  };

  public createConversationMessage = async (
    conversationId: number,
    payload: {
      content: string;
      inReplyToMessageId?: number;
      messageRecommendationId?: number;
      cid?: string;
      context?: {
        taskEntityId?: string | null;
        blockEntityId?: string | null;
      };
    },
    options?: { keepalive?: boolean }
  ): Promise<CreatedMessageResponse> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      `/conversations/${conversationId}/messages`,
      options?.keepalive ? { keepalive: true } : {},
      payload,
      bearerToken
    );
    return this._handleResponse<CreatedMessageResponse>(response);
  };

  public markConversationMessageAsRead = async (
    conversationId: number,
    messageId: number
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      `/conversations/${conversationId}/messages/${messageId}/mark_as_read`,
      {},
      {},
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public markConversationMessageAsIrrelevant = async (
    conversationId: number,
    messageId: number
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      `/conversations/${conversationId}/messages/${messageId}/mark_as_irrelevant`,
      {},
      {},
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public markConversationMessageAsReplied = async (
    conversationId: number,
    messageId: number
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      `/conversations/${conversationId}/messages/${messageId}/mark_as_replied`,
      {},
      {},
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public markConversationMessageAsDeclinedForChatbot = async (
    conversationId: number,
    messageId: number
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      `/conversations/${conversationId}/messages/${messageId}/decline_for_chatbot`,
      {},
      {},
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public cancelChatbotReply = async (
    conversationId: number,
    messageId: number
  ): Promise<CancelChatbotReplyResponse> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      `/chatbots/conversations/${conversationId}/messages/${messageId}/cancel-reply`,
      {},
      {},
      bearerToken
    );
    return this._handleResponse<CancelChatbotReplyResponse>(response);
  };

  public getConversationAttachments = async (
    conversationId: number,
    params: GetAttachmentsParamModel
  ): Promise<{ attachments: any[] }> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(
      `/conversations/${conversationId}/attachments`,
      {},
      params,
      bearerToken
    );
    return this._handleResponse<{ attachments: any[] }>(response);
  };

  public createConversationParticipantPresence = async (
    conversationId: number
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      `/conversations/${conversationId}/presences`,
      {},
      {},
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public markMessageRecommendationAsDeclined = async (
    messageRecommendationId: number
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      `/message_recommendations/${messageRecommendationId}/mark_as_declined`,
      {},
      {},
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  /**
   * LIKES & UNLIKES
   */
  public likeGoal = async (goalEntityId: string): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.likesLike(),
      {},
      {
        type: 'GOAL',
        goalEntityId: goalEntityId
      },
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public unlikeGoal = async (goalEntityId: string): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.likesUnlike(),
      {},
      {
        type: 'GOAL',
        goalEntityId: goalEntityId
      },
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  /**
   * MARKETING
   */
  public createNotificationIntent = async (
    notificationIntent: NotificationIntentModel
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getOptionalAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.notificationIntents(),
      {},
      notificationIntent,
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public getNotificationIntents = async (): Promise<{
    notificationIntents: NotificationIntentModel[];
  }> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(API_ENDPOINTS.notificationIntents(), {}, {}, bearerToken);
    return this._handleResponse<{
      notificationIntents: NotificationIntentModel[];
    }>(response);
  };

  /**
   * SKILL GRAPHS
   */
  public createSkillGraph = async (
    payload: CreateSkillGraphModel
  ): Promise<SuccessCreatedEntity> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(API_ENDPOINTS.skillGraphs(), {}, payload, bearerToken);
    return this._handleResponse<SuccessCreatedEntity>(response);
  };

  public getSkillGraph = async (
    skillGraphEntityId: string,
    maxVersionType?: VersionedEntityType
  ): Promise<SkillGraphModel> => {
    const bearerToken = await this.getAPIToken();
    const params: Record<string, string> = {};
    if (maxVersionType) {
      params.maxVersionType = maxVersionType;
    }
    const response = await this.httpGet(
      API_ENDPOINTS.skillGraph(skillGraphEntityId),
      {},
      params,
      bearerToken
    );
    return this._handleResponse<SkillGraphModel>(response);
  };

  public deleteSkillGraph = async (skillGraphEntityId: string): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpDelete(
      API_ENDPOINTS.skillGraph(skillGraphEntityId),
      {},
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public publishSkillGraph = async (skillGraphEntityId: string): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.skillGraphPublish(skillGraphEntityId),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public createSkillNode = async (
    skillGraphEntityId: string,
    node: SkillNodePatchModel
  ): Promise<SuccessCreatedEntity> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.skillGraphNodes(skillGraphEntityId),
      {},
      node,
      bearerToken
    );
    return this._handleResponse<SuccessCreatedEntity>(response);
  };

  public getSkillNode = async (
    skillGraphEntityId: string,
    nodeEntityId: string,
    maxVersionType?: VersionedEntityType
  ): Promise<SkillNodeModel> => {
    const bearerToken = await this.getAPIToken();
    const params: Record<string, string> = {};
    if (maxVersionType) {
      params.maxVersionType = maxVersionType;
    }
    const response = await this.httpGet(
      API_ENDPOINTS.skillGraphNode(skillGraphEntityId, nodeEntityId),
      {},
      params,
      bearerToken
    );
    return this._handleResponse<SkillNodeModel>(response);
  };

  public updateSkillNode = async (
    skillGraphEntityId: string,
    nodeEntityId: string,
    node: SkillNodePatchModel
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPatch(
      API_ENDPOINTS.skillGraphNode(skillGraphEntityId, nodeEntityId),
      {},
      node,
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public deleteSkillNode = async (
    skillGraphEntityId: string,
    nodeEntityId: string
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpDelete(
      API_ENDPOINTS.skillGraphNode(skillGraphEntityId, nodeEntityId),
      {},
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public createSkillEdge = async (
    skillGraphEntityId: string,
    edge: SkillEdgePatchModel
  ): Promise<SuccessCreatedEntity> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.skillGraphEdges(skillGraphEntityId),
      {},
      edge,
      bearerToken
    );
    return this._handleResponse<SuccessCreatedEntity>(response);
  };

  public getSkillEdge = async (
    skillGraphEntityId: string,
    edgeEntityId: string,
    maxVersionType?: VersionedEntityType
  ): Promise<SkillEdgeModel> => {
    const bearerToken = await this.getAPIToken();
    const params: Record<string, string> = {};
    if (maxVersionType) {
      params.maxVersionType = maxVersionType;
    }
    const response = await this.httpGet(
      API_ENDPOINTS.skillGraphEdge(skillGraphEntityId, edgeEntityId),
      {},
      params,
      bearerToken
    );
    return this._handleResponse<SkillEdgeModel>(response);
  };

  public updateSkillEdge = async (
    skillGraphEntityId: string,
    edgeEntityId: string,
    edge: SkillEdgePatchModel
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPatch(
      API_ENDPOINTS.skillGraphEdge(skillGraphEntityId, edgeEntityId),
      {},
      edge,
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  public deleteSkillEdge = async (
    skillGraphEntityId: string,
    edgeEntityId: string
  ): Promise<SuccessResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpDelete(
      API_ENDPOINTS.skillGraphEdge(skillGraphEntityId, edgeEntityId),
      {},
      bearerToken
    );
    return this._handleResponse<SuccessResponseModel>(response);
  };

  /**
   * PAYMENTS
   */
  public createPaymentSession = async (
    data: PaymentSessionCreateModel
  ): Promise<PaymentSessionModel> => {
    const bearerToken = await this.getOptionalAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.paymentsCreateSession(),
      {},
      data,
      bearerToken
    );
    return this._handleResponse<PaymentSessionModel>(response);
  };

  public getImprvCoinsPackages = async (): Promise<ImprvCoinsPackageModel[]> => {
    const bearerToken = await this.getOptionalAPIToken();
    const response = await this.httpGet(
      API_ENDPOINTS.paymentsImprvCoinsPackages(),
      {},
      {},
      bearerToken
    );
    return this._handleResponse<ImprvCoinsPackageModel[]>(response);
  };

  /**
   * MEDIAITEMS
   */
  public signMediaitemUpload = async (
    contentType: string,
    contentLength: number
  ): Promise<MediaitemSignedUploadModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(
      API_ENDPOINTS.signedUploads(),
      {},
      { contentType, contentLength },
      bearerToken
    );
    return this._handleResponse<MediaitemSignedUploadModel>(response);
  };

  public createMediaitem = async (payload: CreateMediaitemModel): Promise<CreateResponseModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpPost(API_ENDPOINTS.mediaitems(), {}, payload, bearerToken);
    return this._handleResponse<CreateResponseModel>(response);
  };

  public getMediaitem = async (id: number): Promise<MediaitemModel> => {
    const bearerToken = await this.getAPIToken();
    const response = await this.httpGet(API_ENDPOINTS.mediaitemById(id), {}, {}, bearerToken);
    return this._handleResponse<MediaitemModel>(response);
  };

  /**
   * AUTHENTICATION
   */
  public async getAPIToken() {
    let token = null;
    try {
      token = await this.getTokenProvider();
    } catch (e) {
      Sentry.captureException(e);
    }

    if (!token || token === 'undefined' || token === 'null') {
      eventEmitter.emit(EventType.AUTH_SESSION_INVALID, {});
      const error = new Error('No valid auth token');
      error['status'] = 401;
      throw error;
    }

    return token;
  }

  public async getOptionalAPIToken() {
    let token = null;
    try {
      token = await this.getTokenProvider();
    } catch (e) {
      return null;
    }

    if (!token || token === 'undefined' || token === 'null') {
      return null;
    }

    return token;
  }
}

const useImprvApi = () => {
  const { getAccessTokenSilently } = useAuth0();
  return ImprvApiService.getInstance(getAccessTokenSilently);
};

export { ImprvApiService, useImprvApi };
